import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { auth } from '../services/firebase';
import api from '../services/api';
import { updatePassword, sendPasswordResetEmail, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import {
    Video,
    ArrowLeft,
    User,
    Mail,
    Lock,
    Shield,
    LogOut,
    Loader2,
    Download,
    Trash2,
    Database
} from 'lucide-react';

const SettingsPage = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const handlePasswordReset = async () => {
        if (passwords.newPassword !== passwords.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        if (passwords.newPassword.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        if (!passwords.currentPassword) {
            toast.error('Please enter your current password');
            return;
        }

        setLoading(true);
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("Not logged in");

            // Re-authenticate first
            const credential = EmailAuthProvider.credential(currentUser.email, passwords.currentPassword);
            await reauthenticateWithCredential(currentUser, credential);

            // Update password
            await updatePassword(currentUser, passwords.newPassword);

            toast.success('Password updated successfully');
            setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            console.error(error);
            if (error.code === 'auth/wrong-password') {
                toast.error('Incorrect current password');
            } else if (error.code === 'auth/requires-recent-login') {
                toast.error('Please sign in again to change password');
            } else {
                toast.error(error.message || 'Failed to update password');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSendPasswordResetEmail = async () => {
        if (!user?.email) {
            toast.error('No email associated with account');
            return;
        }

        setLoading(true);
        try {
            await sendPasswordResetEmail(auth, user.email);
            toast.success('Password reset email sent! Check your inbox.');
        } catch (error) {
            toast.error(error.message || 'Failed to send reset email');
        } finally {
            setLoading(false);
        }
    };

    const handleExportData = async () => {
        setLoading(true);
        try {
            const response = await api.get('/account/export');
            const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `revealiq-data-${new Date().toISOString().split('T')[0]}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Your data has been exported');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to export data');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAccount = async () => {
        const confirmed = window.confirm(
            'This will permanently delete your account, meetings and AI notes. This cannot be undone. Continue?'
        );
        if (!confirmed) return;

        setLoading(true);
        try {
            await api.delete('/account');
            toast.success('Your account and data have been deleted');
            await logout();
            navigate('/auth');
        } catch (error) {
            // If backend deleted the data but Firebase user needs recent login,
            // guide the user to re-authenticate.
            toast.error(error.response?.data?.detail || 'Failed to delete account. Please sign in again and retry.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-void">
            {/* Header */}
            <header className="border-b border-white/5 bg-void-paper/50 backdrop-blur-xl sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/dashboard')}
                            className="text-grey-400 hover:text-white"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back
                        </Button>
                        <div className="h-6 w-px bg-white/10" />
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-neon-cyan to-neon-magenta flex items-center justify-center">
                                <Video className="w-4 h-4 text-black" />
                            </div>
                            <span className="font-heading font-bold text-white">Settings</span>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 py-12">
                {/* Profile Section */}
                <motion.div
                    className="glass-card p-8 mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <h2 className="font-heading font-semibold text-xl text-white mb-6 flex items-center gap-2">
                        <User className="w-5 h-5 text-neon-cyan" />
                        Profile
                    </h2>

                    <div className="flex items-center gap-6 mb-6">
                        <div className="relative">
                            <Avatar className="w-20 h-20">
                                <AvatarImage src={user?.picture} />
                                <AvatarFallback className="bg-neon-cyan/20 text-neon-cyan text-2xl">
                                    {user?.name?.charAt(0) || 'U'}
                                </AvatarFallback>
                            </Avatar>
                            {user?.provider === 'google.com' && (
                                <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-void-paper border-2 border-void flex items-center justify-center">
                                    <svg className="w-3 h-3" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                </div>
                            )}
                        </div>
                        <div>
                            <h3 className="font-heading font-semibold text-xl text-white">{user?.name}</h3>
                            <p className="text-grey-400 flex items-center gap-2">
                                <Mail className="w-4 h-4" />
                                {user?.email}
                            </p>
                            <p className="text-grey-500 text-sm mt-1">
                                Signed in with {user?.provider === 'google.com' ? 'Google' : 'Email'}
                            </p>
                        </div>
                    </div>

                    <div className="border-t border-white/5 pt-6">
                        <p className="text-grey-400 text-sm">
                            Your profile information is managed by {user?.provider === 'google.com' ? 'Google' : 'Firebase Auth'}.
                            {user?.provider === 'google.com' && ' To change your name or photo, update your Google profile.'}
                        </p>
                    </div>
                </motion.div>

                {/* Security Section */}
                <motion.div
                    className="glass-card p-8 mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <h2 className="font-heading font-semibold text-xl text-white mb-6 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-neon-cyan" />
                        Security
                    </h2>

                    {user?.provider === 'google.com' ? (
                        <div className="text-grey-400">
                            <p className="mb-4">
                                Your account is secured through Google. Password management is handled by your Google account.
                            </p>
                            <a
                                href="https://myaccount.google.com/security"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-neon-cyan hover:underline"
                            >
                                Manage Google Security Settings
                                <ArrowLeft className="w-4 h-4 rotate-180" />
                            </a>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div>
                                <h3 className="font-heading font-medium text-white mb-4 flex items-center gap-2">
                                    <Lock className="w-4 h-4" />
                                    Change Password
                                </h3>
                                <div className="space-y-4 max-w-md">
                                    <div>
                                        <label className="text-sm text-grey-400 mb-1 block">Current Password</label>
                                        <Input
                                            type="password"
                                            placeholder="Enter current password"
                                            value={passwords.currentPassword}
                                            onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                                            className="input-neon"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm text-grey-400 mb-1 block">New Password</label>
                                        <Input
                                            type="password"
                                            placeholder="Enter new password"
                                            value={passwords.newPassword}
                                            onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                                            className="input-neon"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm text-grey-400 mb-1 block">Confirm New Password</label>
                                        <Input
                                            type="password"
                                            placeholder="Confirm new password"
                                            value={passwords.confirmPassword}
                                            onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                                            className="input-neon"
                                        />
                                    </div>
                                    <Button
                                        onClick={handlePasswordReset}
                                        disabled={loading || !passwords.currentPassword || !passwords.newPassword || !passwords.confirmPassword}
                                        className="btn-neon"
                                    >
                                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                        Update Password
                                    </Button>
                                </div>
                            </div>

                            <div className="border-t border-white/5 pt-6">
                                <h3 className="font-heading font-medium text-white mb-2">Forgot Password?</h3>
                                <p className="text-grey-400 text-sm mb-4">
                                    We'll send a password reset link to your email.
                                </p>
                                <Button
                                    variant="outline"
                                    onClick={handleSendPasswordResetEmail}
                                    disabled={loading}
                                    className="border-white/10 text-grey-100 hover:bg-white/5"
                                >
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                                    Send Reset Email
                                </Button>
                            </div>
                        </div>
                    )}
                </motion.div>

                {/* Privacy & Data (DPDP + GDPR) */}
                <motion.div
                    className="glass-card p-8 mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                >
                    <h2 className="font-heading font-semibold text-xl text-white mb-2 flex items-center gap-2">
                        <Database className="w-5 h-5 text-neon-cyan" />
                        Privacy &amp; Data
                    </h2>
                    <p className="text-grey-400 text-sm mb-6">
                        You control your data. Export a copy or permanently erase it, in line with the
                        DPDP Act and GDPR. See our{' '}
                        <a href="/privacy" className="text-neon-cyan hover:underline">Privacy Policy</a>.
                    </p>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/5 pb-6 mb-6">
                        <div>
                            <h3 className="font-heading font-medium text-white mb-1">Export My Data</h3>
                            <p className="text-grey-400 text-sm">Download your account info and meetings as JSON.</p>
                        </div>
                        <Button
                            variant="outline"
                            onClick={handleExportData}
                            disabled={loading}
                            className="border-white/10 text-grey-100 hover:bg-white/5"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                            Export Data
                        </Button>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="font-heading font-medium text-white mb-1">Delete My Account &amp; Data</h3>
                            <p className="text-grey-400 text-sm">Permanently remove your account, meetings and AI notes.</p>
                        </div>
                        <Button
                            variant="outline"
                            onClick={handleDeleteAccount}
                            disabled={loading}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Account
                        </Button>
                    </div>
                </motion.div>

                {/* Danger Zone */}
                <motion.div
                    className="glass-card p-8 border-red-500/20"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <h2 className="font-heading font-semibold text-xl text-red-400 mb-6">
                        Danger Zone
                    </h2>

                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="font-heading font-medium text-white mb-1">Sign Out</h3>
                            <p className="text-grey-400 text-sm">Sign out of your account on this device.</p>
                        </div>
                        <Button
                            variant="outline"
                            onClick={logout}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>
                    </div>
                </motion.div>
            </main>
        </div>
    );
};

export default SettingsPage;
